import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Activity, Heart, Target, Users, Shield, Award } from 'lucide-react';

export default function AboutPage() {
  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="about-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-[#1A4D2E] flex items-center justify-center">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-medium tracking-tight text-stone-900">About ChestBuddy</h1>
            <p className="text-sm text-[#1A4D2E] font-medium">ChestAI GP-Assist</p>
          </div>
        </div>
        <p className="text-stone-500 mt-3 max-w-2xl leading-relaxed">
          ChestBuddy is an AI-powered chest X-ray analysis assistant designed specifically for General Practitioners and small clinics.
          Our mission is to bring advanced radiological decision support to primary healthcare settings.
        </p>
      </div>

      {/* Mission cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-[#1A4D2E]/10 flex items-center justify-center mx-auto mb-3">
              <Target className="w-6 h-6 text-[#1A4D2E]" />
            </div>
            <h3 className="font-heading font-semibold text-stone-900 mb-2">Our Mission</h3>
            <p className="text-sm text-stone-600 leading-relaxed">Make advanced radiology AI accessible to every GP clinic, improving early detection and patient outcomes in primary care.</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-[#1A4D2E]/10 flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6 text-[#1A4D2E]" />
            </div>
            <h3 className="font-heading font-semibold text-stone-900 mb-2">Who We Serve</h3>
            <p className="text-sm text-stone-600 leading-relaxed">GP clinics, small diagnostic centres, and early hospital pilots across India and emerging healthcare markets.</p>
          </CardContent>
        </Card>
        <Card className="border-stone-200 shadow-sm">
          <CardContent className="pt-6 text-center">
            <div className="w-12 h-12 rounded-xl bg-[#1A4D2E]/10 flex items-center justify-center mx-auto mb-3">
              <Shield className="w-6 h-6 text-[#1A4D2E]" />
            </div>
            <h3 className="font-heading font-semibold text-stone-900 mb-2">Safety First</h3>
            <p className="text-sm text-stone-600 leading-relaxed">We are a decision support tool — never a replacement. All findings require physician verification before clinical action.</p>
          </CardContent>
        </Card>
      </div>

      {/* What we actually do - early warning + evident detection */}
      <Card className="border-stone-200 shadow-sm" data-testid="about-positioning">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">Early Warning + Evident Detection</CardTitle>
          <p className="text-xs text-stone-500 mt-1">What ChestBuddyAI actually does — and what it doesn&apos;t</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/40">
              <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider mb-1">10-30% confidence</p>
              <p className="text-sm font-semibold text-stone-900">Early Warning</p>
              <p className="text-xs text-stone-600 mt-1">Subtle infiltrates, small effusions, borderline cardiomegaly, early TB lesions — findings you might overlook on a busy OPD day.</p>
            </div>
            <div className="p-3 rounded-lg border border-orange-200 bg-orange-50/40">
              <p className="text-[10px] font-semibold text-orange-700 uppercase tracking-wider mb-1">35-65% confidence</p>
              <p className="text-sm font-semibold text-stone-900">Likely Finding</p>
              <p className="text-xs text-stone-600 mt-1">Probable pathology with reasoning — worth a confirmation step (repeat X-ray, CT, or labs).</p>
            </div>
            <div className="p-3 rounded-lg border border-red-200 bg-red-50/40">
              <p className="text-[10px] font-semibold text-red-700 uppercase tracking-wider mb-1">70-95% confidence</p>
              <p className="text-sm font-semibold text-stone-900">Evident Pathology</p>
              <p className="text-xs text-stone-600 mt-1">Classic, high-confidence findings — confirms your clinical suspicion + medico-legal documentation.</p>
            </div>
          </div>
          <p className="text-xs text-stone-600 leading-relaxed p-3 rounded-lg bg-stone-50 border border-stone-200">
            <strong className="text-stone-900">Honest disclosure:</strong> ChestBuddyAI catches what&apos;s <em>visible on the chest X-ray</em> — across the full subtle-to-evident spectrum. It does <strong>NOT</strong> predict disease before any radiological sign exists (that needs CT, blood work, or longitudinal screening). It&apos;s a decision-support tool — a second pair of eyes that doesn&apos;t get tired at 8 PM. Final diagnosis stays with the treating physician.
          </p>
        </CardContent>
      </Card>

      {/* What we detect */}
      <Card className="border-stone-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">Conditions We Screen For</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {['Pneumonia', 'Cardiomegaly', 'Pleural Effusion', 'Atelectasis', 'Pneumothorax', 'Pulmonary Nodules', 'Pulmonary Edema', 'Emphysema', 'TB Indicators', 'Fibrosis', 'Consolidation', 'Lung Masses'].map(condition => (
              <div key={condition} className="flex items-center gap-2 p-2 rounded-lg bg-stone-50">
                <div className="w-1.5 h-1.5 rounded-full bg-[#1A4D2E]" />
                <span className="text-sm text-stone-700">{condition}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Features */}
      <Card className="border-stone-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-heading">Key Features</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { icon: Activity, title: 'Multi-Disease AI Analysis', desc: 'GPT-5.2 Vision powered analysis of chest X-rays with per-disease probability scores' },
            { icon: Heart, title: 'Grad-CAM Heatmaps & Bounding Boxes', desc: 'Visual overlays showing exactly where the AI detected abnormalities' },
            { icon: Award, title: 'PDF Reports with Disclaimer', desc: 'Professional reports with findings, recommendations, and medical disclaimer' },
            { icon: Users, title: 'Patient History Tracking', desc: 'Unique Patient IDs, case linking, and complete visit timeline' },
            { icon: Shield, title: 'Audit Logging', desc: 'Every action is logged for compliance and traceability' },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg hover:bg-stone-50 transition-colors">
              <f.icon className="w-5 h-5 text-[#1A4D2E] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-stone-900">{f.title}</p>
                <p className="text-xs text-stone-500 mt-0.5">{f.desc}</p>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <div className="bg-amber-50 border border-amber-200 text-amber-900 px-4 py-3 rounded-lg text-sm">
        <strong>Disclaimer:</strong> ChestBuddy: ChestAI GP-Assist is a decision support system and does not provide medical diagnoses. Always consult qualified medical professionals for clinical decisions.
      </div>
    </div>
  );
}
